import { createReducer, Action, on } from '@ngrx/store';
import { isEqual, uniqBy } from 'lodash-es';

import { FormulaActions } from './formula.actions';
import { FormulaState } from './formula.state';
import { QualityTrafficLights } from '../../features/shared/models/common/quality-traffic-lights.enum';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { AppActions } from '../app/app.actions';

export const initialState: FormulaState = {
  loading: false,
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(
      FormulaActions.LoadFormulaSuccess,
      (state, action): FormulaState => ({
        ...state,
        list: [action.formula, ...(state.list || []).filter((a) => a.id !== action.formula.id)],
      })
    ),
    on(
      AppActions.LoadInitDataSuccess,
      (state, action): FormulaState => ({
        ...state,
        formulaPositions: action.data?.assetSettings?.recentFormulaPositions || {},
        selectedTransactionId:
          action?.data?.assetSettings?.lastSelectedTransactionForFormula ||
          action?.data?.transactions.find(
            (t) => t?.qualityTrafficLight === QualityTrafficLights.Green
          )?.transactionId,
      })
    ),
    on(
      FormulaActions.UpdateFormula,
      (state): FormulaState => ({ ...state, loading: true, error: undefined })
    ),
    on(
      FormulaActions.UpdateFormulaSuccess,
      (state, action): FormulaState => ({
        ...state,
        loading: false,
        selectedFormulaVersionId: undefined,
        list: state.list?.map((f) => (f.id === action.formula.id ? action.formula : f)),
      })
    ),
    on(
      FormulaActions.UpdateFormulaFail,
      FormulaActions.SyncContextFail,
      (state, action): FormulaState => ({ ...state, loading: false, error: action.error })
    ),

    on(FormulaActions.SelectFormula, (state, action): FormulaState => {
      const previousId = state.selectedFormulaId;
      return {
        ...state,
        selectedFormulaId: action.id,
        selectedFormulaVersionId: undefined,
        list:
          previousId && action.previousVariables
            ? state.list?.map((f) =>
                f.id === state.selectedFormulaId
                  ? {
                      ...f,
                      variables: action.previousVariables,
                      unsaved: !isEqual(action.previousVariables, f.variables) || f.unsaved,
                    }
                  : f
              )
            : state.list,
      };
    }),
    on(
      FormulaActions.SetTransaction,
      (state, action): FormulaState => ({ ...state, selectedTransactionId: action.transactionId })
    ),
    on(
      FormulaActions.SelectFormulaVersionSuccess,
      (state, action): FormulaState => ({
        ...state,
        selectedFormulaVersionId: action.formulaVersionId,
        list: action.formulaVersionId
          ? state.list.map((f) => {
              if (f.id === state.selectedFormulaId) {
                return {
                  ...f,
                  versions: f.versions.map((v) =>
                    v.id === action.formulaVersionId ? { ...v, ...action.formulaVersion } : v
                  ),
                };
              }
              return f;
            })
          : state.list,
      })
    ),
    on(
      FormulaActions.VariablesChanged,
      (state, action): FormulaState => ({
        ...state,
        list: state.selectedFormulaId
          ? state.list?.map((f) =>
              f.id === state.selectedFormulaId
                ? {
                    ...f,
                    variables: action.variables,
                    unsaved: !isEqual(action.variables, f.variables) || f.unsaved,
                  }
                : f
            )
          : state.list,
      })
    ),
    on(
      FormulaActions.SyncContext,
      (state, action): FormulaState => ({
        ...state,
        loading: !!state.selectedTransactionId,
        contextVariables: [],
      })
    ),
    on(
      FormulaActions.FormulaPositionChanged,
      (state, action): FormulaState => ({
        ...state,
        formulaPositions: {
          ...(state.formulaPositions || {}),
          [action.event.formulaId]: action.event.position,
        },
      })
    ),
    on(
      FormulaActions.SyncContextSuccess,
      (state, action): FormulaState => ({
        ...state,
        contextVariables: action.variables,
        loading: false,
      })
    ),
    on(
      FormulaActions.RemoveRecentFormulas,
      (state, action): FormulaState => ({
        ...state,
        selectedFormulaId:
          action.id === state.selectedFormulaId ? undefined : state.selectedFormulaId,
        list: state.list?.filter((f) => f.id !== action.id),
      })
    ),
    on(
      FormulaActions.CodeChanged,
      (state, action): FormulaState => ({
        ...state,
        list: state.selectedFormulaId
          ? state.list.map((f) =>
              f.id === state.selectedFormulaId
                ? {
                    ...f,
                    script: action.code,
                    unsaved: !isEqual(action.code, f.script) || f.unsaved,
                  }
                : f
            )
          : state.list,
      })
    ),
    on(FormulaActions.LoadRecentFormulasSuccess, (state, action): FormulaState => {
      const currentList = state.list || [];
      const unsaved = currentList.filter((f) => f.unsaved) || [];

      return {
        ...state,
        list: uniqBy([...currentList, ...action.formulas], (f) => f.id),
      };
    })
  )
);

export function reducer(state: FormulaState | undefined, action: Action) {
  return featureReducer(state, action);
}
